// The answer poster sits level with the cast (owner, 13 October): never smaller
// than that layout's play tile, and where the cast block — the top of "Cast" to
// the last role — is taller, the poster grows until its 2:3 height matches it.
//
// It is measured rather than written as CSS because the two depend on each
// other: a wider poster leaves the cast less width, the cast wraps more and
// gets taller, which asks for a wider poster. CSS resolves that loop badly
// (CLAUDE.md records a 343px poster from trying), so this walks it up from the
// tile size until a step changes nothing, and sets the answer as `--cs-fit`.
//
// **The block is measured from its own contents, never from the grid's rows**:
// a poster taller than the cast stretches the rows it spans, and reading those
// back would ask for a taller poster every time — a loop with no end.
import { useLayoutEffect, type RefObject } from 'react';

const STEPS = 12;

/** Heading plus each row's tallest entry, margins included — what the cast
 *  takes up when nothing is stretching it. */
function castHeight(reveal: HTMLElement): number {
  const heading = reveal.querySelector<HTMLElement>('.cs-cast-heading');
  if (!heading) return 0;
  const outer = (el: Element | undefined) => {
    if (!el) return 0;
    const box = el.getBoundingClientRect();
    if (box.height === 0) return 0;               // the hidden second column
    const cs = getComputedStyle(el);
    return box.height + parseFloat(cs.marginTop) + parseFloat(cs.marginBottom);
  };
  const col1 = [...reveal.querySelectorAll('.cs-cast-col1 > .cs-cast-row')];
  const col2 = [...reveal.querySelectorAll('.cs-cast-col2 > .cs-cast-row')];
  let total = outer(heading);
  for (let i = 0; i < Math.max(col1.length, col2.length); i++) {
    total += Math.max(outer(col1[i]), outer(col2[i]));
  }
  return total;
}

export function usePosterFit(ref: RefObject<HTMLElement | null>, key: unknown) {
  useLayoutEffect(() => {
    const reveal = ref.current;
    if (!reveal) return;
    let busy = false;
    const fit = () => {
      if (busy) return;
      busy = true;
      // From the tile up, so a screen that has grown can give a smaller answer.
      reveal.style.setProperty('--cs-fit', '0px');
      let last = 0;
      for (let i = 0; i < STEPS; i++) {
        const width = Math.ceil(castHeight(reveal) * 2 / 3 * 2) / 2;
        if (Math.abs(width - last) < 0.5) break;
        last = width;
        reveal.style.setProperty('--cs-fit', `${width}px`);
      }
      busy = false;
    };
    fit();
    // Width changes (rotation, a resized window, the font arriving) re-fit; the
    // fit's own change of height settles on the same answer and stops there.
    const observer = new ResizeObserver(() => fit());
    observer.observe(reveal);
    document.fonts?.ready.then(fit).catch(() => {});
    return () => observer.disconnect();
  }, [ref, key]);
}
